<template>
  <div class="pay-ranges-table">
    <div v-if="loading" class="prt-loading">
      <div
        v-for="n in pageInfo.pageSize || 5"
        :key="n"
        class="prt-skeleton-row"
      >
        <v-skeleton-loader type="text" class="prt-skeleton" />
        <v-skeleton-loader type="text" class="prt-skeleton" />
        <v-skeleton-loader type="text" class="prt-skeleton" />
        <v-skeleton-loader type="text" class="prt-skeleton" />
        <v-skeleton-loader type="text" class="prt-skeleton" />
        <v-skeleton-loader type="text" class="prt-skeleton" />
        <v-skeleton-loader type="text" class="prt-skeleton" />
      </div>
    </div>

    <div v-else-if="error" class="prt-error" role="alert">
      <p class="prt-error__message">We couldn't load pay ranges. Please try again.</p>
      <button type="button" class="prt-error__retry" @click="$emit('retry')">Retry</button>
    </div>

    <NoRecordFound
      v-else-if="!rows.length"
      title="Pay Ranges"
      message="Pay Ranges"
      :showSpecificMessage="emptySpecificMessage"
      :description="emptyDescription"
      :showClearAction="showFilteredEmpty"
      :clearLabel="clearFilterLabel"
      :shouldShowAddAction="false"
      :noBorder="true"
      :showActiveCounter="false"
      :hideCounter="true"
      btnTitle="Pay Range"
      class="prt-no-record"
      @onClear="$emit('clear-filters')"
    />

    <DxTreeList
      v-else
      ref="treelist"
      :data-source="treeRows"
      :show-row-lines="true"
      :show-borders="false"
      :show-column-lines="false"
      key-expr="code"
      parent-id-expr="parentId"
      has-items-expr="has_items"
      :auto-expand-all="false"
      :allow-search="false"
      :word-wrap-enabled="true"
      :column-auto-width="true"
      class="prt-treelist"
    >
      <DxScrolling mode="standard" />
      <DxColumn
        data-field="code"
        caption="Code"
        cell-template="codeCell"
        :allow-sorting="false"
        :width="100"
      />
      <template #codeCell="{ data: options }">
        <div class="hide-left-border hide-right-border center-vertical align-start">
          <span class="prt-code">{{ options.data.code }}</span>
        </div>
      </template>

      <DxColumn
        data-field="name"
        caption="Pay range"
        cell-template="nameCell"
        :allow-sorting="false"
        :min-width="220"
      />
      <template #nameCell="{ data: options }">
        <div
          class="hide-left-border hide-right-border center-vertical align-start"
          :class="{ 'prt-muted': options.data.muted }"
        >
          <button
            type="button"
            class="prt-name-btn"
            @click="$emit('open-detail', { id: options.data.id })"
          >
            <PayRangeHeader
              :country-flag="options.data.country_flag"
              :pay-range-name="options.data.name"
              :pay-range-location="payRangeLocation(options.data)"
              :pay-range-currency-code="options.data.currency"
              :pay-range-expressed-as="options.data.pay_basis"
              :hover-enabled="true"
            />
          </button>
        </div>
      </template>

      <DxColumn
        data-field="readiness"
        caption="Pay Range vs Readiness"
        cell-template="readinessCell"
        header-cell-template="readinessHeader"
        :allow-sorting="false"
        :min-width="240"
      />
      <template #readinessHeader>
        <span class="prt-header__readiness">
          Pay Range vs Readiness
          <br />
          @&nbsp;0%&nbsp;-&nbsp;50%&nbsp;-&nbsp;100%&nbsp;-&nbsp;150%
        </span>
      </template>
      <template #readinessCell="{ data: options }">
        <div
          class="hide-left-border hide-right-border center-vertical align-start"
          :class="{ 'prt-muted': options.data.muted }"
        >
          <PayRangeReadinessAmounts
            class="prt-readiness"
            :min="getAnchor(options.data, 0)"
            :at50="getAnchor(options.data, 50)"
            :at100="getAnchor(options.data, 100)"
            :max="getAnchor(options.data, 150)"
          />
        </div>
      </template>

      <DxColumn
        data-field="version_label"
        caption="Version"
        cell-template="versionCell"
        :allow-sorting="false"
        :min-width="150"
      />
      <template #versionCell="{ data: options }">
        <div
          class="hide-left-border hide-right-border center-vertical align-start"
          :class="{ 'prt-muted': options.data.muted }"
        >
          <div
            v-if="options.data.version_label && options.data.version_history"
            class="prt-version"
          >
            <div class="prt-version__heading">
              <VersionBadge :code="options.data.version_label" />
              <PayPlanPeriodStateBadge
                v-if="options.data.version_state"
                :state="options.data.version_state"
              />
            </div>
            <span v-if="options.data.version_date_label" class="prt-version__date">
              {{ options.data.version_date_label }}
            </span>
            <span v-if="options.data.draft_line_label" class="prt-version__draft">
              {{ options.data.draft_line_label }}
            </span>
            <span v-else-if="options.data.next_line_label" class="prt-version__date">
              {{ options.data.next_line_label }}
            </span>
          </div>
          <div v-else-if="options.data.version_label" class="prt-version">
            <span class="prt-version__badge">{{ options.data.version_label }}</span>
            <span v-if="options.data.version_date_label" class="prt-version__date">
              {{ options.data.version_date_label }}
            </span>
            <span v-if="options.data.draft_line_label" class="prt-version__draft">
              {{ options.data.draft_line_label }}
            </span>
            <span v-else-if="options.data.next_line_label" class="prt-version__date">
              {{ options.data.next_line_label }}
            </span>
          </div>
          <span v-else-if="options.data.version_empty_label" class="prt-version__none">
            {{ options.data.version_empty_label }}
          </span>
          <span v-else class="prt-dash">-</span>
        </div>
      </template>

      <DxColumn
        data-field="positions_in_use"
        :caption="positionsHeader"
        cell-template="positionsCell"
        header-cell-template="positionsHeader"
        :allow-sorting="false"
        :width="90"
      />
      <template #positionsHeader>
        <span v-if="versionHistory" class="prt-header__positions">Positions</span>
        <span v-else class="prt-header__positions">Used by<br />Positions</span>
      </template>
      <template #positionsCell="{ data: options }">
        <div class="hide-left-border hide-right-border center-vertical">
          <a
            v-if="positionsLink(options.data)"
            href="#"
            class="prt-positions prt-positions--link"
            :aria-label="`View ${positionsDisplay(options.data)} positions using ${options.data.code}`"
            @click.prevent="
              $emit('open-positions', { id: options.data.id, query: options.data.positions_query })
            "
          >
            {{ positionsDisplay(options.data) }}
          </a>
          <span v-else class="prt-positions">{{ positionsDisplay(options.data) }}</span>
        </div>
      </template>

      <DxColumn
        data-field="status"
        caption="Status"
        cell-template="statusCell"
        :allow-sorting="false"
        :width="176"
      />
      <template #statusCell="{ data: options }">
        <div class="hide-left-border hide-right-border center-vertical prt-status-cell">
          <div class="prt-status-badges">
            <OrgObjectStatusBadge
              :status="statusBadgeProps(options.data).status"
              :label="statusBadgeProps(options.data).label"
            />
            <OrgObjectStatusBadge
              v-if="archivedTagOf(options.data)"
              neutral
              :label="archivedTagOf(options.data).label"
            />
          </div>
          <span
            v-if="options.data.show_draft_pending_pill"
            class="prt-draft-pending-pill"
          >
            Draft pending
          </span>
        </div>
      </template>

      <DxColumn
        data-field="chatter"
        caption=""
        cell-template="chatterCell"
        :allow-sorting="false"
        :width="56"
      />
      <template #chatterCell="{ data: options }">
        <div class="hide-left-border hide-right-border center-vertical">
          <button
            class="prt-actions__chatter"
            type="button"
            aria-label="Open chatter"
            @click="
              $emit('open-chatter', {
                id: options.data.id,
                name: options.data.name,
                code: options.data.code,
              })
            "
          >
            <ChatterIcon class="prt-actions__chatter-icon" />
          </button>
        </div>
      </template>

      <DxColumn
        data-field="actions"
        caption=""
        cell-template="actionsCell"
        :allow-sorting="false"
        :width="56"
      />
      <template #actionsCell="{ data: options }">
        <div class="hide-left-border hide-right-border center-vertical align-end">
          <div class="prt-actions">
            <DropDownMenu
              :dropdown-items="payRangeRowMenuItems(options.data)"
              :is-open="false"
              :translate-y-offset="30"
              :translate-x-offset="12"
              @onClick="onRowAction(options.data, $event)"
            >
              <template #activator>
                <button class="prt-actions__menu" type="button" aria-label="More options">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                      stroke="#344054"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z"
                      stroke="#344054"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z"
                      stroke="#344054"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </template>
            </DropDownMenu>
          </div>
        </div>
      </template>
    </DxTreeList>

    <ExPagination
      v-if="rows.length > 0"
      :pagination="pagination"
      @onRequest="onChangePage"
    />
  </div>
</template>

<script setup>
import { computed } from "vue";
import { DxTreeList, DxColumn, DxScrolling } from "devextreme-vue/tree-list";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import ChatterIcon from "@components/icons/items/BadgeHolderTable/ChatterIcon.vue";
import DropDownMenu from "@components/DropDownMenu/DropDownMenu.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import VersionBadge from "@components/VersionBadge/VersionBadge.vue";
import PayPlanPeriodStateBadge from "@components/PayPlanPeriodStateBadge/PayPlanPeriodStateBadge.vue";
import PayRangeHeader from "@components/PayRangeHeader/PayRangeHeader.vue";
import PayRangeReadinessAmounts from "@components/PayRangeReadinessAmounts/PayRangeReadinessAmounts.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import { formatPayRangeAmount } from "@components/utils/formatPayRangeAmount";
import { payRangeRowMenuItems } from "@components/PayRangesListPage/payRangeRowMenuItems";
import { archivedTag, rangeStatusBadge } from "@components/FairPay/payRangeBadges";

const STATUSES_WITHOUT_POSITIONS = ["draft", "retired", "archived"];

const TONE_TO_BADGE_STATUS = { draft: "draft", "in-force": "active", terminated: "retired" };

function statusBadgeProps(row) {
  const badge = row.status_badge || rangeStatusBadge(row.status);
  return badge
    ? { status: TONE_TO_BADGE_STATUS[badge.tone], label: badge.label }
    : { status: row.status, label: undefined };
}

function archivedTagOf(row) {
  return row.status_badge ? row.archived_tag : archivedTag(row.status);
}

const props = defineProps({
  rows: {
    type: Array,
    required: true,
  },
  pageInfo: {
    type: Object,
    required: true,
    // { count: Number, page: Number, pageSize: Number }
  },
  showReferenceCurrency: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /** True when the last list request failed — shows the error message with a Retry button. */
  error: {
    type: Boolean,
    default: false,
  },
  /** idea-MDP-429 — Figma presentation (version badge + state, "Positions" header, linked counts). */
  versionHistory: {
    type: Boolean,
    default: false,
  },
  /** True when search and/or non-default filters are applied (filtered empty state). */
  hasActiveSearchOrFilters: {
    type: Boolean,
    default: false,
  },
  clearFilterLabel: {
    type: String,
    default: "Clear Filter",
  },
});

const emit = defineEmits([
  "row-action",
  "open-chatter",
  "open-detail",
  "open-positions",
  "retry",
  "page-change",
  "clear-filters",
]);

const showFilteredEmpty = computed(() => !!props.hasActiveSearchOrFilters);

const emptySpecificMessage = computed(() =>
  showFilteredEmpty.value ? "No Pay Ranges found" : ""
);

const emptyDescription = computed(() =>
  showFilteredEmpty.value
    ? "Your filter did not match any Pay Ranges.<br/>Please try again."
    : "Create your first pay range to get started."
);

const pagination = computed(() => ({
  page: props.pageInfo.page,
  total: props.pageInfo.count,
  perPage: props.pageInfo.pageSize,
}));

const treeRows = computed(() =>
  (props.rows || []).map((row) => ({
    ...row,
    parentId: null,
    has_items: false,
  }))
);

function payRangeLocation(row) {
  if (row.region) {
    return `${row.operating_country} - ${row.region}`;
  }
  return row.operating_country || "";
}

function getAnchor(row, pct) {
  if (props.showReferenceCurrency) {
    const key = `ref_anchor_${pct}`;
    return formatPayRangeAmount(row[key] ?? "-");
  }
  const key = `anchor_${pct}`;
  return formatPayRangeAmount(row[key] ?? "-");
}

const positionsHeader = computed(() => (props.versionHistory ? "Positions" : "Used by Positions"));

function hasNoPositions(row) {
  return STATUSES_WITHOUT_POSITIONS.includes((row.status || "").toLowerCase());
}

function positionsDisplay(row) {
  if (hasNoPositions(row)) {
    return props.versionHistory ? "–" : "-";
  }
  return row.positions_in_use ?? 0;
}

function positionsLink(row) {
  return props.versionHistory && !hasNoPositions(row) && !!row.positions_query;
}

function onRowAction(row, item) {
  emit("row-action", { id: row.id ?? row.code, action: item.value });
}

function onChangePage({ page }) {
  emit("page-change", { page });
}
</script>

<style scoped>
.pay-ranges-table {
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
  overflow-x: auto;
  overflow-y: hidden;
}

.prt-treelist {
  min-width: 100%;
  overflow-x: visible;
}

/* ── DxTreeList shell ──────────────────────── */
.prt-treelist :deep(.dx-treelist-headers .dx-treelist-table .dx-row > td) {
  background-color: #f9fafb;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  font-family: Inter, sans-serif;
  padding: 12px 16px;
  color: #667085;
  border-left: none !important;
  border-right: none !important;
  vertical-align: middle;
}

.prt-treelist :deep(.dx-treelist-borders .dx-treelist-rowsview),
.prt-treelist :deep(.dx-treelist-headers + .dx-treelist-rowsview),
.prt-treelist :deep(.dx-treelist-rowsview.dx-treelist-after-headers),
.prt-treelist :deep(.dx-treelist-borders > .dx-treelist-headers),
.prt-treelist :deep(.dx-treelist-borders > .dx-treelist-rowsview) {
  border: none !important;
}

.prt-treelist :deep(.dx-treelist-rowsview .dx-row > td) {
  border-left: none !important;
  border-right: none !important;
  border-bottom: 1px solid #eaecf0 !important;
  border-top: none !important;
  padding: 16px;
  vertical-align: middle;
}

.prt-treelist :deep(.dx-treelist-icon-container) {
  display: none !important;
}

.prt-treelist :deep(.dx-treelist-empty-space) {
  display: none !important;
}

.prt-treelist :deep(td:has(> div.hide-left-border)) {
  border-left: none !important;
}

.prt-treelist :deep(td:has(> div.hide-right-border)) {
  border-right: none !important;
}

.prt-treelist :deep(td:has(> div.center-vertical)) {
  vertical-align: middle !important;
}

.prt-treelist :deep(.align-start) {
  text-align: start !important;
}

.prt-treelist :deep(.align-end) {
  display: flex;
  justify-content: flex-end;
}

/* ── Header ────────────────────────────────── */
.prt-header__readiness {
  display: block;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.prt-header__positions {
  display: block;
  text-align: center;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
}

/* ── Code cell ─────────────────────────────── */
.prt-name-btn {
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.prt-code {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #101828;
  white-space: nowrap;
}

/* ── Version cell ──────────────────────────── */
.prt-version {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.prt-version__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 16px;
  background: #f2f4f7;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
}

.prt-version__heading {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.prt-muted {
  opacity: 0.6;
}

.prt-version__none {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.prt-version__date {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.prt-version__draft {
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #c11574;
  white-space: nowrap;
}

/* ── Status cell ───────────────────────────── */
.prt-treelist :deep(.dx-treelist-rowsview .dx-row > td:has(> .prt-status-cell)) {
  padding-top: 12px;
  padding-bottom: 12px;
}

.prt-treelist :deep(.prt-status-cell) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.prt-status-badges {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
}

.prt-draft-pending-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border: 1px solid #fcceee;
  border-radius: 16px;
  background: #fdf2fa;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #c11574;
  white-space: nowrap;
}

/* ── Dash / positions ──────────────────────── */
.prt-dash,
.prt-positions {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
  text-align: center;
  display: block;
}

.prt-positions--link {
  color: #6941c6;
  text-decoration: none;
  cursor: pointer;
}

.prt-positions--link:hover {
  text-decoration: underline;
}

.prt-positions--link:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
  border-radius: 4px;
}

/* ── Error state ───────────────────────────── */
.prt-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 240px;
  padding: 24px;
}

.prt-error__message {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.prt-error__retry {
  padding: 8px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #ffffff;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}

.prt-error__retry:hover {
  background: #f9fafb;
}

.prt-error__retry:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

/* ── Actions cell ──────────────────────────── */
.prt-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-end;
}

.prt-actions__chatter,
.prt-actions__menu {
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #667085;
}

.prt-actions__chatter:hover,
.prt-actions__menu:hover {
  background: #f9fafb;
}

.prt-actions__chatter-icon {
  width: 20px;
  height: 21px;
}

/* ── Skeleton rows ─────────────────────────── */
.prt-loading {
  border-top: 1px solid #eaecf0;
}

.prt-skeleton-row {
  display: grid;
  grid-template-columns: 100px 1.5fr 1.2fr 120px 100px 100px 88px;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid #eaecf0;
}

.prt-no-record {
  min-height: 320px;
}
</style>
